import * as React from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import { Status } from 'api/common/statuses'
import { completeDealPanaceaPayCheckAPI } from 'api/completeDeal'
import { PaymentMethod } from 'api/common/constants'
import { PurchasePanaceaPayResponse } from 'api/common/responses'
import { PositionSimplified } from 'api/common/types'
import { purchasePanaceaPayAPI } from 'api/purchase'
import Footer from 'components/Footer'
import Header from 'components/Header'
import loginData from 'utilities/loginData'
import { getBankAccountTypeLabel, getPositionLabel } from 'utilities/resources/options'
import { numberFormat } from 'utilities/typeUtility'
import { PositionInfo } from 'utilities/resources/formItemValues'

type StateValue = {
  contractPositions?: PositionSimplified[]
  showPositions?: PositionInfo[]
}

const SceneComponent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const stateValue = React.useMemo(() => (location.state ?? {}) as StateValue, [location])
  const { contractPositions, showPositions } = stateValue
  const [bankInfo, setBankInfo] = React.useState<PurchasePanaceaPayResponse>()
  const [isSpinningSubmit, setIsSpinningSubmit] = React.useState(false)
  const positionNumbers = React.useMemo(() => contractPositions?.map((data) => data.positionNumber) ?? [], [contractPositions])

  /**
   * 「入金確認をする」ボタン
   */
  const handlePaymentCheckButton = async () => {
    setIsSpinningSubmit(true)
    await completeDealPanaceaPayCheckAPI({ positionNumbers }, (isPayed) => {
      if (isPayed) {
        navigate('/aubeQuestDealPurchaseComplete')
      }
    })
    setIsSpinningSubmit(false)
  }

  React.useEffect(() => {
    if (!loginData.isLoggedIn()) {
      navigate('/')
    } else if (!contractPositions) {
      navigate('/positionContract')
    } else {
      purchasePanaceaPayAPI({ positions: contractPositions }, (status, response) => {
        if (status === Status.Success) {
          setBankInfo(response)
          loginData.setPurchasePositionData({ positionNumbers, amount: response.amount })
        } else {
          navigate('/positionContract')
        }
      })
    }
  }, [navigate, contractPositions, positionNumbers])
  return (
    <>
      <Header title="AUBEクエストディール購入" />
      <main>
        <section className="mx-4 my-5">
          <div>
            <div>
              下記の口座情報先にページ下部の金額を<span className="text-danger fw-bold">ご自身の口座からお振込みください。</span>
              お振込みの約10分～30分後に「入金確認をする」のボタンを押してください。入金確認が完了したら購入完了です。
            </div>
            <div className="mt-3">
              ※振込先は株式会社イー・コミュニケーションズの収納会社（パナシア）になります。
              <br />
              また、この口座は<span className="text-danger fw-bold">本決済のみに（今回限り）使用出来る口座です。</span>
              <br />
              別の決済や他人はご使用になれませんのでご注意下さい。
            </div>
          </div>
          <section className="mt-5">
            <h3 className="fw-bold">決済ポジション</h3>
            {showPositions?.map((position, index) => (
              <div key={index}>
                <div className="fw-bold mt-4">{getPositionLabel(position.positionNumber)}</div>
                <div className="mt-2">コネクターID : {position.positionId}</div>
                <div className="mt-2">AUBEクエストID : {position.atemQuestId}</div>
                <hr className="mt-4 my-2" />
              </div>
            ))}
          </section>
          <section className="mt-5">
            <h3 className="fw-bold">口座情報</h3>
            <div className="fw-bold mt-1">銀行コード：{bankInfo?.bankCode ?? ''}</div>
            <div className="fw-bold mt-1">銀行名：{bankInfo?.bankName ?? ''}</div>
            <div className="fw-bold mt-1">支店コード：{bankInfo?.branchCode ?? ''}</div>
            <div className="fw-bold mt-1">支店名：{bankInfo?.branchName ?? ''}</div>
            <div className="fw-bold mt-1">口座種別：{getBankAccountTypeLabel(bankInfo?.accountType ?? '')}</div>
            <div className="fw-bold mt-1">口座番号：{bankInfo?.accountNumber ?? ''}</div>
            <div className="fw-bold mt-1">振込先：{bankInfo?.accountNameKana ?? ''}</div>
          </section>
          <section className="mt-5">
            <div>AUBEクエストディール</div>
            <div className="fw-bold mt-1">価格&nbsp;&nbsp;&nbsp;¥{numberFormat(bankInfo?.amount)}（税込）</div>
          </section>
          <div className="mt-5">
            <Button variant="primary" className="w-100 py-4" disabled={isSpinningSubmit || !bankInfo} onClick={handlePaymentCheckButton}>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" hidden={!isSpinningSubmit} />
              入金確認をする
            </Button>
          </div>
          <div className="mt-5 text-center">
            <Button variant="link" onClick={() => navigate('/paymentSelect', { state: { contractPositions, showPositions, previousScene: PaymentMethod.BankTransfer } })}>
              支払い方法を変更する
            </Button>
          </div>
          <div className="mt-5 text-center">
            <Button variant="link" href="/positionContract">
              戻る
            </Button>
          </div>
        </section>
      </main>
      <Footer className="mt-5" />
    </>
  )
}

export default SceneComponent
